import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  TextField,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import axiosInstance from "../../lib/axios";
import { useLocation } from "react-router-dom";
import { NavContext } from "../../Pages/ServiceProvDash/ServiceProvDrawer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Styled ListItem for professional card-like appearance
const StyledListItem = styled(ListItem)(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: theme.spacing(1),
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  border: "1px solid #E5E7EB",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
    transform: "translateY(-2px)",
  },
}));

const AcceptTasks = () => {
  const theme = useTheme();
  const location = useLocation();
  const { setNavValue } = useContext(NavContext);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [rejectingTaskId, setRejectingTaskId] = useState(null);
  const [rejectionRemarks, setRejectionRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set navigation value when component mounts
  useEffect(() => {
    if (location.pathname === "/tasks/accept" && typeof setNavValue === "function") {
      setNavValue(3);
    }
  }, [location.pathname, setNavValue]);

  const getStatusChip = (status) => {
    let color, label;
    switch (status) {
      case "O":
        label = "Open";
        color = "warning";
        break;
      case "R":
        label = "Resolved";
        color = "success";
        break;
      case "C":
        label = "Closed";
        color = "error";
        break;
      default:
        label = "Unknown";
        color = "default";
        break;
    }
    return { label, color };
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPendingTasks = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post(
          "TrnTicketing/GetTrnTicketDashBoard",
          {
            start: 0,
            PageSize: 10,
            SearchText: 2,
            FromDate: "2025-10-09",
            ToDate: "2025-10-09",
          }
        );

        if (isMounted && response.data.Status === 0 && response.data.Data) {
          const tasks = response.data.Data.map((item) => {
            const ticketDate = new Date(item.TktDate);
            return {
              id: item.TicketId,
              ticketNo: item.TicketNo ? item.TicketNo.replace(/^#/, "") : "N/A",
              status: item.TicketSt || "N/A",
              serviceName: item.ServiceName || "N/A",
              raisedBy: item.Name || "Unknown",
              date: ticketDate.toISOString().split("T")[0],
              time: ticketDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              room: item.RoomNo || item.CommonAreaName || "N/A",
              priority: item.TicketSeverityId === 0 ? "Medium" : item.TicketSeverityId === 1 ? "High" : "Low",
            };
          });
          setPendingTasks(tasks);
        } else if (isMounted) {
          setError("Failed to fetch data: " + response.data.Message);
        }
      } catch (err) {
        if (isMounted) {
          setError("Error fetching data: " + err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPendingTasks();
    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize the task list to prevent unnecessary re-renders
  const memoizedTasks = useMemo(() => pendingTasks, [pendingTasks]);

  const handleAccept = async (id) => {
    try {
      const response = await axiosInstance.post(
        "TrnTicketing/AcceptTrnTicketByServProvider",
        {
          TicketId: id,
          AcceptFlg: "Y",
          Reason: "",
          UpdatedBy: 2,
        }
      );
      console.log("Accept API Response:", response.data);
      if (response.data.Status === 0) {
        toast.success(response.data.Message || `Task ${id} accepted successfully.`);
        setPendingTasks((prev) => prev.filter((task) => task.id !== id));
      } else {
        console.error(response.data.Message);
        toast.error(response.data.Message || "Failed to accept task.");
      }
    } catch (err) {
      console.error("Accept Error:", err.message);
      toast.error("Error accepting task: " + err.message);
    }
  };

  const handleReject = (id) => {
    setRejectingTaskId(id);
  };

  const handleSubmitRejection = async (id) => {
    if (!rejectionRemarks.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }

    try {
      const response = await axiosInstance.post(
        "TrnTicketing/AcceptTrnTicketByServProvider",
        {
          TicketId: id,
          AcceptFlg: "N",
          Reason: rejectionRemarks.trim(),
          UpdatedBy: 2,
        }
      );
      console.log("Reject API Response:", response.data);
      if (response.data.Status === 0) {
        toast.success(response.data.Message || `Task ${id} rejected successfully.`);
        setPendingTasks((prev) => prev.filter((task) => task.id !== id));
        setRejectingTaskId(null);
        setRejectionRemarks("");
      } else {
        console.error(response.data.Message);
        toast.error(response.data.Message || "Failed to reject task.");
      }
    } catch (err) {
      console.error("Reject Error:", err.message);
      toast.error("Error rejecting task: " + err.message);
    }
  };

  const handleCancelRejection = () => {
    console.log("Cancel clicked, resetting rejection form");
    setRejectingTaskId(null);
    setRejectionRemarks("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          color: "error.main",
        }}
      >
        <Typography variant="h6" component="div">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: { xs: "flex", sm: "none" },
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        pb: 12,
        maxWidth: 600,
        mx: "auto",
        bgcolor: "#F9FAFB",
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFB, #E5E7EB)",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
      {/* Title */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: '"Roboto", sans-serif',
            fontSize: { xs: "1.4rem" },
            lineHeight: 1.4,
          }}
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          Accept Tasks
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: "#6B7280",
            fontFamily: '"Roboto", sans-serif',
            fontSize: "0.9rem",
            mt: 0.5,
            lineHeight: 1.4,
          }}
        >
          Review and accept or reject pending tasks
        </Typography>
      </Box>

      <List sx={{ width: "100%" }}>
        {memoizedTasks.map((task, index) => {
          const { label, color } = getStatusChip(task.status);
          return (
            <StyledListItem
              key={task.id}
              component={motion.div}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#111827",
                      fontSize: { xs: "1.1rem" },
                      maxWidth: "100%",
                      whiteSpace: "normal",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4,
                    }}
                  >
                    Ticket No. {task.ticketNo}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#6B7280", display: "block" }}
                    >
                      Ticket For: {task.serviceName}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.9rem", fontWeight: 600, display: "block" }}
                    >
                      Priority: {task.priority}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.9rem", display: "block" }}
                    >
                      Raised by: {task.raisedBy} | Room: {task.room}
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.85rem", display: "block" }}
                    >
                      Date: {task.date} | Time: {task.time}
                    </Typography>
                    <Chip
                      component="span" // Render Chip as a span to avoid div nesting
                      label={label}
                      color={color}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: "0.85rem", display: "inline-block" }}
                    />
                  </>
                }
              />
              <Box sx={{ mt: 1.5, width: "100%" }}>
                {rejectingTaskId === task.id ? (
                  <>
                    <TextField
                      label="Reason for Rejection"
                      value={rejectionRemarks}
                      onChange={(e) => setRejectionRemarks(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      sx={{ mb: 1, bgcolor: "#FFFFFF", borderRadius: 1 }}
                      aria-label={`Rejection reason for task ${task.id}`}
                      InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                    />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CheckIcon fontSize="small" />}
                        onClick={() => handleSubmitRejection(task.id)}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          maxWidth: "100px",
                          flex: 1,
                          "&:hover": {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                        aria-label={`Submit rejection for task ${task.id}`}
                      >
                        Submit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleCancelRejection}
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          maxWidth: "100px",
                          flex: 1,
                          "&:hover": {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          },
                        }}
                        aria-label={`Cancel rejection for task ${task.id}`}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon fontSize="small" />}
                      onClick={() => handleAccept(task.id)}
                      sx={{
                        borderRadius: "6px",
                        fontSize: "0.85rem",
                        maxWidth: "100px",
                        "&:hover": {
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        },
                      }}
                      aria-label={`Accept task ${task.id}`}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CloseIcon fontSize="small" />}
                      onClick={() => handleReject(task.id)}
                      sx={{
                        borderRadius: "6px",
                        fontSize: "0.85rem",
                        maxWidth: "100px",
                        "&:hover": {
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        },
                      }}
                      aria-label={`Reject task ${task.id}`}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
              </Box>
            </StyledListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default AcceptTasks;